import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Pagination, Form, InputGroup, Button } from "react-bootstrap";
import InitialLoader from "components/InitilalLoader";
import NoData from "components/NoData/NoData";
import "./DataTable.scss";

const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  pagination: paginationConfig = { enabled: true, pageSize: 10 },
  sorting: sortingConfig = { enabled: true },
  filtering: filteringConfig = { enabled: false },
  rowSelection: rowSelectionConfig = { enabled: false },
  onRowClick,
  emptyMessage = "No data available",
  keyField = "id",
  striped = true,
  bordered = true,
  hover = true,
  responsive = true,
  globalFilter: globalFilterValue,
  onGlobalFilterChange,
  enableGlobalFilter = false,
}) => {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(globalFilterValue || "");
  const [rowSelection, setRowSelection] = useState({});

  // Handle global filter (controlled or uncontrolled)
  const effectiveGlobalFilter =
    globalFilterValue !== undefined ? globalFilterValue : globalFilter;
  const handleGlobalFilterChange = (value) => {
    if (onGlobalFilterChange) {
      onGlobalFilterChange(value);
    } else {
      setGlobalFilter(value);
    }
  };

  // Table instance configuration
  const tableConfig = {
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(paginationConfig?.enabled && {
      getPaginationRowModel: paginationConfig?.manual
        ? undefined
        : getPaginationRowModel(),
      manualPagination: paginationConfig?.manual || false,
      pageCount: paginationConfig?.pageCount,
    }),
    ...(sortingConfig?.enabled && {
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: setSorting,
      enableSorting: true,
      manualSorting: sortingConfig?.manual || false,
    }),
    ...(filteringConfig?.enabled && {
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      enableColumnFilters: true,
      manualFiltering: filteringConfig?.manual || false,
    }),
    ...(rowSelectionConfig?.enabled && {
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      getRowId: (row) => row[keyField] || row.id,
    }),
    ...(enableGlobalFilter && {
      globalFilterFn: "includesString",
      onGlobalFilterChange: handleGlobalFilterChange,
    }),
    state: {
      ...(sortingConfig?.enabled && { sorting }),
      ...(filteringConfig?.enabled && { columnFilters }),
      ...(rowSelectionConfig?.enabled && { rowSelection }),
      ...(enableGlobalFilter && { globalFilter: effectiveGlobalFilter }),
      ...(paginationConfig?.enabled && {
        pagination: {
          pageIndex: paginationConfig?.pageIndex || 0,
          pageSize: paginationConfig?.pageSize || 10,
        },
      }),
    },
  };

  const table = useReactTable(tableConfig);

  // Handle row selection changes
  React.useEffect(() => {
    if (rowSelectionConfig?.enabled && rowSelectionConfig?.onSelectionChange) {
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      rowSelectionConfig.onSelectionChange(selectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, rowSelectionConfig]);

  // Calculate totalCount for pagination (needed for useMemo hook)
  const totalCount = paginationConfig?.manual
    ? paginationConfig?.totalCount || 0
    : table.getRowCount();

  // Calculate pageSizeOptions - must be before early returns to follow Rules of Hooks
  const pageSizeOptions = useMemo(() => {
    const options = [10, 20, 30, 50, 100, 250, 500, 750, 1000].filter(
      (size) => size <= totalCount
    );
    // Ensure at least one option is available
    return options.length > 0 ? options : [10];
  }, [totalCount]);

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    if (paginationConfig?.onPageChange) {
      paginationConfig.onPageChange(newPage);
    } else {
      table.setPageIndex(newPage - 1);
    }
  };

  const handlePrevPage = () => {
    const currentPage = table.getState().pagination.pageIndex + 1;
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const currentPage = table.getState().pagination.pageIndex + 1;
    const totalPages = table.getPageCount();
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePageSizeChange = (newSize) => {
    const newPageSize = Number(newSize);

    // If manual pagination and onPageSizeChange callback exists, call it
    if (paginationConfig?.manual && paginationConfig?.onPageSizeChange) {
      paginationConfig.onPageSizeChange(newPageSize);
    }

    // Only update table's internal state if NOT in manual mode
    if (!paginationConfig?.manual) {
      table.setPageSize(newPageSize);
    }
  };

  if (loading) {
    return <InitialLoader />;
  }

  // Don't show empty state if columns are not defined
  if (!columns || columns.length === 0) {
    return <div className="text-muted">No columns defined</div>;
  }

  if (!data || data.length === 0) {
    return <NoData message={emptyMessage} />;
  }

  const tableClasses = [
    "table",
    striped && "table-striped",
    bordered && "table-bordered",
    hover && "table-hover",
  ]
    .filter(Boolean)
    .join(" ");

  const TableWrapper = responsive ? "div" : React.Fragment;
  const wrapperProps = responsive ? { className: "table-responsive" } : {};

  return (
    <div className="data-table-wrapper">
      {enableGlobalFilter && (
        <div className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={effectiveGlobalFilter}
              onChange={(e) => handleGlobalFilterChange(e.target.value)}
            />
            {effectiveGlobalFilter && (
              <Button
                variant="outline-secondary"
                onClick={() => handleGlobalFilterChange("")}
              >
                Clear
              </Button>
            )}
          </InputGroup>
        </div>
      )}

      <TableWrapper {...wrapperProps}>
        <Table className={tableClasses}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                    }}
                    className={header.column.getCanSort() ? "sortable" : ""}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="d-flex align-items-center">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getCanSort() && (
                        <span className="ms-2">
                          {{
                            asc: " ↑",
                            desc: " ↓",
                          }[header.column.getIsSorted()] ?? " ⇅"}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowClick && onRowClick(row.original)}
                className={onRowClick ? "clickable-row" : ""}
                style={onRowClick ? { cursor: "pointer" } : {}}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      {paginationConfig?.enabled && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="d-flex align-items-center">
            <span className="me-2">Show:</span>
            <Form.Select
              value={
                paginationConfig?.manual
                  ? paginationConfig?.pageSize || 10
                  : table.getState().pagination.pageSize
              }
              onChange={(e) => handlePageSizeChange(e.target.value)}
              style={{ width: "auto" }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Form.Select>
            <span className="ms-2">
              {(() => {
                if (paginationConfig?.manual) {
                  const pageIndex = paginationConfig.pageIndex;
                  const pageSize = paginationConfig.pageSize;
                  const totalCount = paginationConfig.totalCount;

                  if (totalCount === 0) {
                    return "No entries";
                  }

                  const start = pageIndex * pageSize + 1;
                  const end = Math.min((pageIndex + 1) * pageSize, totalCount);
                  return `Showing ${start} to ${end} of ${totalCount} entries`;
                } else {
                  // Fallback to table values when NOT manual
                  if (table.getRowModel().rows.length > 0) {
                    const pageIndex = table.getState().pagination.pageIndex;
                    const pageSize = table.getState().pagination.pageSize;
                    const totalCount = table.getRowCount();
                    const start = pageIndex * pageSize + 1;
                    const end = Math.min(
                      (pageIndex + 1) * pageSize,
                      totalCount
                    );
                    return `Showing ${start} to ${end} of ${totalCount} entries`;
                  }
                  return "No entries";
                }
              })()}
            </span>
          </div>

          <Pagination>
            {(() => {
              // Use config values when manual, otherwise use table values
              const isManual = paginationConfig?.manual;
              const pageIndex = isManual
                ? paginationConfig?.pageIndex
                : table.getState().pagination.pageIndex;
              const pageCount = isManual
                ? paginationConfig?.pageCount
                : table.getPageCount();
              const currentPage = pageIndex + 1;
              const canPreviousPage = isManual
                ? pageIndex > 0
                : table.getCanPreviousPage();
              const canNextPage = isManual
                ? pageIndex + 1 < pageCount
                : table.getCanNextPage();

              return (
                <>
                  <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={!canPreviousPage}
                  />
                  <Pagination.Prev
                    onClick={handlePrevPage}
                    disabled={!canPreviousPage}
                  />
                  {(() => {
                    if (pageCount <= 7) {
                      // Show all pages if 7 or fewer
                      return [...Array(pageCount)].map((_, idx) => {
                        const page = idx + 1;
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      });
                    } else {
                      // Show pagination with ellipsis for more pages
                      const pages = [];
                      pages.push(
                        <Pagination.Item
                          key={1}
                          active={1 === currentPage}
                          onClick={() => handlePageChange(1)}
                        >
                          1
                        </Pagination.Item>
                      );

                      if (currentPage > 3) {
                        pages.push(
                          <Pagination.Ellipsis key="ellipsis-start" />
                        );
                      }

                      const startPage = Math.max(2, currentPage - 1);
                      const endPage = Math.min(pageCount - 1, currentPage + 1);

                      for (let page = startPage; page <= endPage; page++) {
                        pages.push(
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      }

                      if (currentPage < pageCount - 2) {
                        pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                      }

                      pages.push(
                        <Pagination.Item
                          key={pageCount}
                          active={pageCount === currentPage}
                          onClick={() => handlePageChange(pageCount)}
                        >
                          {pageCount}
                        </Pagination.Item>
                      );

                      return pages;
                    }
                  })()}
                  <Pagination.Next
                    onClick={handleNextPage}
                    disabled={!canNextPage}
                  />
                  <Pagination.Last
                    onClick={() => handlePageChange(pageCount)}
                    disabled={!canNextPage}
                  />
                </>
              );
            })()}
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default DataTable;
